import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Frontignan' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Frontignan',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Frontignan' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Frontignan. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Vos pièces sont-elles adaptées au climat marin de Frontignan ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Tout à fait. Pour Frontignan, nous utilisons des pièces traitées contre la corrosion : roulettes, galets, axes de volet, visserie inox. Cela vaut pour les baies coulissantes comme pour les volets roulants et les stores.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pouvez-vous intervenir rapidement à Frontignan-Plage ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons dans tout Frontignan, y compris Frontignan-Plage et la Peyrade, sous 24 à 48h. Les résidences en front de mer nécessitent un entretien plus fréquent ; nous pouvons programmer des visites préventives.',
      },
    },
  ],
};

export default function FrontignanPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Frontignan | DFPRO"
        description="Baie coulissante, roulettes ou volet en panne à Frontignan ? Air marin et sel accélèrent l'usure. DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-frontignan/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Frontignan"
        subtitle="Roulettes de baie rongées par le sel, volet roulant bloqué par le vent : nos techniciens interviennent à Frontignan, la Peyrade et Frontignan-Plage. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Roulettes de baie usées par le sel à Frontignan ?"
        ctaText="Pièces anticorrosion, devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens connaissent les contraintes du littoral frontignanais."
        ctaServiceLabel="Nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Frontignan, entre mer et étang d'Ingril, expose ses menuiseries à un air chargé en sel qui corrode roulettes, galets et mécanismes. Nous utilisons des pièces traitées anticorrosion pour chaque intervention. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/frontignan-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Frontignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : roulettes corrodées par le sel</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes et galets</Link> sont les pièces qui souffrent le plus à Frontignan. Le sel attaque le métal, le roulement grippe, la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince ou déraille</Link>. Ne forcez pas.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous remplaçons les pièces par des modèles renforcés, réparons le <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link> encrassé par le sable et assurons le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>. <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Remplacement</Link> si nécessaire.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant bloqué par le vent à Frontignan</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Volets bloqués</Link> après des coups de vent, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteurs</Link> qui surchauffent à forcer contre un tablier gonflé par l'humidité : nous diagnostiquons et réparons sur place.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous assurons aussi la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels avec moteurs protégés, le <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">changement de sangle</Link> et tout le <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">dépannage volet</Link>.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/frontignan-volet-02.jpg"
              alt="Réparation de volet roulant à Frontignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/frontignan-vitrage-03.jpg"
              alt="Remplacement de vitrage à Frontignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage et fenêtre : étanchéité contre le vent marin</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Notre service <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">vitrier</Link> couvre le <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">remplacement de vitre cassée</Link>, la pose de <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> et le traitement des <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrages embués</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> dégradés, <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>. Stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link> et <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Frontignan sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Frontignan : centre-ville, la Peyrade, Frontignan-Plage. Également Vic-la-Gardiole, Gigean, Balaruc. Consultez <Link to="/depannage-menuiserie-sete/" className="text-accent-500 font-semibold hover:underline">Sète</Link> et <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}